import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, CircularProgress, InputAdornment, MenuItem,
  Paper, Typography,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import dealLogo from '../../assests/drawerIcons/dealLogo.svg'
import {
  EntityFormDrawer,
  EntityFormSection,
  EntityDrawerInput,
  EntityDrawerSelect,
  EntityFormField,
  entityDrawerInputSx,
} from '../shared/EntityFormDrawer'
import { dealsApi, DEAL_PRIORITY_LABELS, DEAL_SOURCE_LABELS } from '../../lib/api/dealsApi'
import { customersApi } from '../../lib/api/customersApi'
import { getCustomerTypes, type CustomerType } from '../../lib/api/customerTypesApi'
import { themeColors } from '../../app/layout/theme'
import { FormInput } from '../ui'

const PRIORITY_OPTIONS = Object.entries(DEAL_PRIORITY_LABELS).map(([k, v]) => ({ label: v, value: k }))
const SOURCE_OPTIONS   = Object.entries(DEAL_SOURCE_LABELS).map(([k, v]) => ({ label: v, value: k }))

export interface DealDrawerValues {
  dealName:            string
  stage:               string
  amount:              string
  currency:            string
  probabilityPct:      string
  priority:            string
  source:              string
  customerTypeId?:     string
  expectedClosingDate: string
  description:         string
  requirements:        string
  customerId:          string
  contactId:           string
  orgId?:              string
  lobId?:              string
}

const DEFAULTS: DealDrawerValues = {
  dealName: '', stage: '', amount: '', currency: 'INR',
  probabilityPct: '', priority: 'MEDIUM', source: '', customerTypeId: '', expectedClosingDate: '',
  description: '', requirements: '', customerId: '', contactId: '', orgId: '', lobId: '',
}

interface DealDrawerProps {
  open:          boolean
  onClose:       () => void
  onSubmit:      (data: DealDrawerValues) => void
  isSubmitting?: boolean
  initialData?:  Partial<DealDrawerValues>
  initialCustomer?: { id: string; name: string }
}

export function DealDrawer({
  open, onClose, onSubmit, isSubmitting = false, initialData, initialCustomer,
}: DealDrawerProps) {
  const [form, setForm] = useState<DealDrawerValues>({ ...DEFAULTS, ...initialData })
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerList, setShowCustomerList] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof DealDrawerValues, string>>>({})

  useEffect(() => {
    if (open) {
      setForm({ ...DEFAULTS, ...initialData })
      setCustomerSearch(initialCustomer?.name ?? '')
      setErrors({})
    }
  }, [open, initialData, initialCustomer])

  const set = (k: keyof DealDrawerValues, v: string) => {
    setForm(f => ({ ...f, [k]: v }))
    setErrors(e => ({ ...e, [k]: '' }))
  }



  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn:  getCustomerTypes,
    enabled:  open,
  })

  const { data: customersData, isFetching: customerFetching } = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn:  () => customersApi.getAll({ search: customerSearch || undefined, page: 1, pageSize: 20 }),
    enabled:  open && showCustomerList,
    placeholderData: prev => prev,
  })
  const customers = customersData?.items ?? []

  const { data: contacts = [] } = useQuery({
    queryKey: ['customer-contacts', form.customerId],
    queryFn:  () => customersApi.getContacts(form.customerId),
    enabled:  open && !!form.customerId,
  })

  const contactOptions = contacts.map(c => ({
    label: `${c.name}${c.role ? ` (${c.role})` : ''}`,
    value: c.id,
  }))

  const selectedCustomer = customers.find(c => c.id === form.customerId)
  const customerDisplayName = selectedCustomer
    ? (selectedCustomer.companyName || selectedCustomer.name)
    : (form.customerId && initialCustomer?.id === form.customerId ? initialCustomer.name : customerSearch)

  const validate = () => {
    const e: typeof errors = {}
    if (!form.dealName.trim()) e.dealName = 'Deal name is required'
    if (!form.customerId) e.customerId = 'Customer is required'
    if (!form.customerTypeId) e.customerTypeId = 'Customer type is required'
    if (!form.amount || parseFloat(form.amount) <= 0) e.amount = 'Amount is required'
    if (!form.expectedClosingDate) e.expectedClosingDate = 'Expected close date is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  return (
    <EntityFormDrawer
      open={open}
      title={initialData?.dealName ? 'Edit Deal' : 'New Deal'}
      icon={<img src={dealLogo} alt="deal" style={{ width: 20, height: 20 }} />}
      onClose={onClose}
      onSubmit={() => { if (validate()) onSubmit(form) }}
      submitLabel={initialData?.dealName ? 'Save Changes' : 'Create Deal'}
      isSubmitting={isSubmitting}
      submitDisabled={
        !form.dealName.trim() ||
        !form.customerId ||
        !form.customerTypeId ||
        !form.amount ||
        parseFloat(form.amount) <= 0 ||
        !form.expectedClosingDate
      }
    >

      <EntityFormSection title="Deal Info">

        <EntityDrawerInput
          label="Deal Name" required fullWidth
          value={form.dealName}
          onChange={v => set('dealName', v)}
          placeholder="e.g. Enterprise License Q3"
          error={errors.dealName}
        />

        <EntityDrawerInput
          label="Amount (INR)" required
          value={form.amount}
          onChange={v => set('amount', v)}
          placeholder="Enter amount"
          type="number"
          error={errors.amount}
        />

        <EntityDrawerInput
          label="Expected Close Date" required
          value={form.expectedClosingDate}
          onChange={v => set('expectedClosingDate', v)}
          type="date"
          error={errors.expectedClosingDate}
          inputProps={{
            min: (() => {
              const now = new Date()
              const tzOffset = now.getTimezoneOffset() * 60000
              return new Date(now.getTime() - tzOffset).toISOString().split('T')[0]
            })()
          }}
        />

        <EntityDrawerInput
          label="Probability %"
          value={form.probabilityPct}
          onChange={v => set('probabilityPct', v)}
          placeholder="e.g. 70"
          type="number"
        />

        <EntityDrawerSelect
          label="Priority"
          value={form.priority}
          onChange={v => set('priority', v)}
          options={PRIORITY_OPTIONS}
        />

        <EntityDrawerSelect
          label="Source"
          value={form.source}
          onChange={v => set('source', v)}
          options={SOURCE_OPTIONS}
          placeholder="Select source"
        />

        <EntityDrawerSelect
          label="Customer Type"
          required
          value={form.customerTypeId ?? ''}
          onChange={v => set('customerTypeId', v)}
          options={customerTypes.map(ct => ({ label: ct.name, value: String(ct.id) }))}
          placeholder="Select customer type"
          error={errors.customerTypeId}
        />

        <EntityDrawerInput
          label="Description" fullWidth
          value={form.description}
          onChange={v => set('description', v)}
          placeholder="Add deal notes or context..."
          multiline
          minRows={3}
        />

        <EntityFormField label="Requirements" fullWidth>
          <FormInput
            label="Requirements" fullWidth multiline rows={3}
            value={form.requirements}
            onChange={e => set('requirements', e.target.value)}
            placeholder="Enter deal requirements..."
          />
        </EntityFormField>

      </EntityFormSection>

      <EntityFormSection title="Customer & Contact">

        <EntityFormField label="Customer" required fullWidth>
          <Box sx={{ position: 'relative' }}>
            <FormInput
              placeholder="Search customer…"
              value={customerDisplayName}
              onChange={e => {
                setCustomerSearch(e.target.value)
                setShowCustomerList(true)
                if (!e.target.value) {
                  set('customerId', '')
                  set('contactId', '')
                }
              }}
              onFocus={() => setShowCustomerList(true)}
              onBlur={() => setTimeout(() => setShowCustomerList(false), 200)}
              error={!!errors.customerId}
              helperText={errors.customerId}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: themeColors.text.muted }} />
                  </InputAdornment>
                ),
                endAdornment: customerFetching
                  ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                  : null,
              }}
              fullWidth
            />
            {showCustomerList && customers.length > 0 && (
              <Paper elevation={3} sx={{
                position: 'absolute', zIndex: 1400, width: '100%', mt: 0.5,
                maxHeight: 180, overflowY: 'auto', borderRadius: '8px',
              }}>
                {customers.map(c => (
                  <Box
                    key={c.id}
                    onMouseDown={() => {
                      set('customerId', c.id)
                      set('contactId', '')
                      if (c.orgId) set('orgId', c.orgId)
                      if (c.lobId) set('lobId', c.lobId)
                      setCustomerSearch('')
                      setShowCustomerList(false)
                    }}
                    sx={{
                      px: 2, py: 1, cursor: 'pointer',
                      bgcolor: form.customerId === c.id ? themeColors.primaryLight + '25' : 'transparent',
                      '&:hover': { bgcolor: themeColors.primaryLight + '15' },
                    }}
                  >
                    <Typography fontWeight={600} fontSize="13px">{c.companyName || c.name}</Typography>
                    <Typography fontSize="11px" color="text.secondary">{c.phone}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Box>

          {selectedCustomer && (selectedCustomer.address || selectedCustomer.pincode || selectedCustomer.city || selectedCustomer.gst) && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <Typography fontSize="12px" fontWeight={600} color="text.primary">
                Company Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {selectedCustomer.address && (
                  <Typography fontSize="11.5px" color="text.secondary">
                    <strong>Address:</strong> {selectedCustomer.address}
                  </Typography>
                )}
                {selectedCustomer.pincode && (
                  <Typography fontSize="11.5px" color="text.secondary">
                    <strong>PIN Code:</strong> {selectedCustomer.pincode}
                  </Typography>
                )}
                {selectedCustomer.city && (
                  <Typography fontSize="11.5px" color="text.secondary">
                    <strong>City:</strong> {selectedCustomer.city}
                  </Typography>
                )}
                {selectedCustomer.gst && (
                  <Typography fontSize="11.5px" color="text.secondary">
                    <strong>GSTIN:</strong> {selectedCustomer.gst}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}
        </EntityFormField>

        {form.customerId && (
          <EntityDrawerSelect
            label="Primary Contact" fullWidth
            value={form.contactId}
            onChange={v => set('contactId', v)}
            options={contactOptions}
            placeholder={contacts.length === 0 ? 'No contacts found' : 'Select contact'}
            disabled={contacts.length === 0}
          />
        )}

      </EntityFormSection>

    </EntityFormDrawer>
  )
}
